'use client';

/**
 * First-touch acquisition attribution. Captured once (the very first app load,
 * pre-signup) and stored in localStorage; UserInitializer passes it to Convex on
 * account creation so we can measure LTV/revenue by channel.
 */
export interface FirstTouch {
  source?: string;
  medium?: string;
  campaign?: string;
  landing?: string;
}

const KEY = 'stx_first_touch';

export function captureFirstTouch(): void {
  if (typeof window === 'undefined') return;
  try {
    if (localStorage.getItem(KEY)) return; // first-touch only — never overwrite
    const p = new URLSearchParams(location.search);
    const ft: FirstTouch = {};
    const src = p.get('utm_source');
    const med = p.get('utm_medium');
    const camp = p.get('utm_campaign');
    if (src) ft.source = src;
    if (med) ft.medium = med;
    if (camp) ft.campaign = camp;
    if (!ft.source && document.referrer) {
      try {
        const host = new URL(document.referrer).host;
        if (host && !host.includes(location.host)) {
          ft.source = host;
          ft.medium = ft.medium || 'referral';
        }
      } catch {
        /* ignore */
      }
    }
    if (!ft.source) ft.source = 'direct';
    ft.landing = location.pathname;
    localStorage.setItem(KEY, JSON.stringify(ft));
  } catch {
    /* ignore */
  }
}

export function getFirstTouch(): FirstTouch | undefined {
  if (typeof window === 'undefined') return undefined;
  try {
    const s = localStorage.getItem(KEY);
    return s ? (JSON.parse(s) as FirstTouch) : undefined;
  } catch {
    return undefined;
  }
}


const REF_KEY = 'stx_referral_code';

/** Friend-referral code from ?ref= — kept separately from first-touch so a
 *  returning visitor's code still applies at signup. First code wins. */
export function captureReferralCode(): void {
  if (typeof window === 'undefined') return;
  try {
    if (localStorage.getItem(REF_KEY)) return;
    const ref = new URLSearchParams(location.search).get('ref');
    if (ref && /^[A-Z2-9]{4,12}$/i.test(ref)) {
      localStorage.setItem(REF_KEY, ref.toUpperCase());
    }
  } catch {}
}

export function getReferralCode(): string | undefined {
  if (typeof window === 'undefined') return undefined;
  try {
    return localStorage.getItem(REF_KEY) || undefined;
  } catch {
    return undefined;
  }
}
